import React, { useRef, useState } from "react";
import SelectPetsSection from "../../../components/customer/SelectPetsSection";
import {
    packagesData,
    dogbreedOptionsMap,
    getBookingQuestions,
} from "../../../utils/Customer/CustomerData";
import { useNavigate } from "react-router-dom";
import { Check, ChevronLeft, ChevronRight } from "lucide-react";
import QuestionCard, { QuestionType } from "../../../components/customer/QuestionCard";
import PackageCard from "../../../components/customer/PackageCard";
import { useAlert } from "../../../components/customer/AlertContext";

const PetTrainingScreen: React.FC = () => {
    const { showConfirm } = useAlert();
    const navigate = useNavigate();

    // Slider horizontal element controls anchors
    const trainingSliderRef = useRef<HTMLDivElement>(null);
    const questionCardRef = useRef<HTMLDivElement>(null);

    // Core dynamic sliding engine indexing tracking
    const [currentStep, setCurrentStep] = useState<number>(0);

    const [selectedSavedPet, setSelectedSavedPet] = useState<any>(() => {
        const savedActive = localStorage.getItem("selected_active_pet");
        return savedActive ? JSON.parse(savedActive) : undefined;
    });

    const [questionStep, setQuestionStep] = useState(0);
    const [showQuestionCard, setShowQuestionCard] = useState(false);

    const [pets, setPets] = useState<any[]>(() => {
        const savedPets = localStorage.getItem("customer_pets");
        if (savedPets) {
            try { return JSON.parse(savedPets); } catch (e) { console.error(e); }
        }
        return [
            { id: "saved_dog_1", name: "Max (Saved)", type: "dog", size: "large", image: "https://images.unsplash.com/photo-1503256207526-0d5d80fa2f47?w=300" }
        ];
    });

    const [answers, setAnswers] = useState<{
        petType: string;
        petName: string;
        breed: string;
        gender: string;
        weight: string;
        petImage: string | File;
    }>({
        petType: "dog",
        petName: "",
        breed: "",
        gender: "",
        weight: "",
        petImage: "",
    });
    const [customBreeds, setCustomBreeds] = useState<any[]>([]);

    const rawQuestions = getBookingQuestions("dog");
    const bookingQuestions = rawQuestions.filter(q => q.key !== "petType"); // Strip explicit classification loops

    const profilesFilteredByCategory = pets
        .filter(pet => pet.type?.toLowerCase() === "dog")
        .map(pet => ({
            ...pet,
            image: pet.image || pet.petImage,
            petImage: pet.petImage || pet.image
        }));

    const handleSavedPetSelect = (pet: any) => {
        const normalizedPet = {
            ...pet,
            image: pet.image || pet.petImage,
            petImage: pet.petImage || pet.image
        };
        setSelectedSavedPet(normalizedPet);
        localStorage.setItem("selected_active_pet", JSON.stringify(normalizedPet));
        setCurrentStep(1); // Advance to Packages view
        window.scrollTo({ top: 0, behavior: "auto" });
    };

    const handleStepHeaderNavigation = (targetStep: number) => {
        if (targetStep === 0) {
            setCurrentStep(0);
        } else if (targetStep === 1 && selectedSavedPet) {
            setCurrentStep(1);
        }
        window.scrollTo({ top: 0, behavior: "auto" });
    };

    const handleAnswer = (value: any) => {
        const currentQuestion = bookingQuestions[questionStep];
        if (currentQuestion.key === "breed" && typeof value === "string") {
            const exists = customBreeds.some(item => item.value === value);
            if (!exists) {
                setCustomBreeds(prev => [...prev, { id: value, label: value, value }]);
            }
        }
        setAnswers(prev => ({ ...prev, [currentQuestion.key]: value }));
    };

    const handleContinue = () => {
        if (questionStep < bookingQuestions.length - 1) {
            setQuestionStep(prev => prev + 1);
            return;
        }

        const savePetProfile = (finalImageString: string) => {
            const defaultImage = "https://images.unsplash.com/photo-1503256207526-0d5d80fa2f47?w=150";

            const newPet = {
                id: Date.now().toString(),
                name: answers.petName,
                type: "dog",
                breed: answers.breed,
                gender: answers.gender,
                image: finalImageString || defaultImage,
                size: answers.weight || "standard",
            };

            setPets(prev => {
                const updatedPets = [...prev, newPet];
                localStorage.setItem("customer_pets", JSON.stringify(updatedPets));
                return updatedPets;
            });

            showConfirm({
                title: "🐾 Profile Created Successfully!",
                message: `Awesome! ${answers.petName} has been added to your squad.`,
                type: "success",
                confirmText: "Let's See Training Packages",
                showCancel: false,
            });

            setSelectedSavedPet(newPet);
            setShowQuestionCard(false);
            localStorage.setItem("selected_active_pet", JSON.stringify(newPet));

            setAnswers({ petType: "dog", petName: "", breed: "", gender: "", weight: "", petImage: "" });
            setQuestionStep(0);
            setCurrentStep(1); // Move directly to packages stage

            window.scrollTo({ top: 0, behavior: "auto" });
        };

        if (answers.petImage instanceof File) {
            const reader = new FileReader();
            reader.onloadend = () => savePetProfile(reader.result as string);
            reader.readAsDataURL(answers.petImage);
        } else {
            savePetProfile(answers.petImage);
        }
    };

    const currentQuestion = bookingQuestions[questionStep];
    let currentOptions = currentQuestion?.options || [];
    if (currentQuestion?.key === "breed") {
        currentOptions = [...dogbreedOptionsMap, ...customBreeds];
    }

    const handleAddPetClick = () => {
        setShowQuestionCard(true);
        setQuestionStep(0);
        setAnswers(prev => ({ ...prev, petType: "dog" }));

        setTimeout(() => {
            questionCardRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
        }, 150);
    };

    const currentPetSize = selectedSavedPet?.size || answers.weight;
    const filteredTrainingPackages = packagesData.filter((pkg) => pkg.validFor.includes("dog"));

    const scrollSlider = (direction: "left" | "right") => {
        if (trainingSliderRef.current) {
            const clientWidth = trainingSliderRef.current.clientWidth;
            const scrollAmount = direction === "left" ? -clientWidth : clientWidth;
            trainingSliderRef.current.scrollBy({ left: scrollAmount, behavior: "smooth" });
        }
    };

    const stepsData = [
        { label: "Select Dog Profile", description: "Who is getting trained" },
        { label: "Training Packages", description: "Select class module structures" }
    ];

    return (
        <div className="pb-20 w-full bg-gray-50/50 min-h-screen">
            <div className="max-w-7xl mx-auto px-4 relative z-30 pt-6">

                {/* GLOBAL INTERACTIVE PROGRESS STEPPER INDICATOR BAR */}
                <div className="bg-white rounded-3xl p-5 shadow-xl shadow-gray-100/40 border border-gray-100 mb-8 max-w-5xl mx-auto">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 md:gap-2">
                        {stepsData.map((stepItem, idx) => {
                            const isCompleted = currentStep > idx;
                            const isActive = currentStep === idx;
                            const isClickable = idx === 0 ? true : (idx === 1 && !!selectedSavedPet);

                            return (
                                <React.Fragment key={idx}>
                                    <div
                                        onClick={() => isClickable && handleStepHeaderNavigation(idx)}
                                        className={`flex items-center gap-3 select-none ${isClickable ? "cursor-pointer group" : "cursor-default"}`}
                                    >
                                        <div className={`w-9 h-9 rounded-full flex items-center justify-center font-bold text-sm transition-all duration-300 ${isCompleted ? "bg-emerald-500 text-white" : isActive ? "bg-mainBrandColor text-white ring-4 ring-orange-100" : "bg-gray-100 text-gray-400"
                                            }`}>
                                            {isCompleted ? <Check size={16} strokeWidth={3} /> : idx + 1}
                                        </div>
                                        <div>
                                            <h4 className={`text-sm font-semibold transition-colors ${isActive ? "text-mainBrandColor" : isCompleted ? "text-gray-800 group-hover:text-mainBrandColor" : "text-gray-400"}`}>
                                                {stepItem.label}
                                            </h4>
                                            <p className="text-[11px] text-gray-400 font-normal hidden sm:block">{stepItem.description}</p>
                                        </div>
                                    </div>
                                    {idx < 1 && (
                                        <div className={`hidden md:block h-[2px] flex-1 mx-4 rounded-full transition-colors duration-500 ${currentStep > idx ? "bg-emerald-500" : "bg-gray-100"}`} />
                                    )}
                                </React.Fragment>
                            );
                        })}
                    </div>
                </div>

                {/* SLIDING CANVAS VIEWSTAGE */}
                <div className={`relative w-full pb-10 ${currentStep === 1 ? "overflow-visible" : "overflow-hidden"}`}>
                    <div
                        className="flex transition-transform duration-500 ease-in-out w-[200%]"
                        style={{
                            transform: `translateX(-${currentStep * 50}%)`
                        }}
                    >

                        {/* STAGE VIEW 1: ONLY DOG PROFILES LIST & BUILDER */}
                        <div className="w-1/2 px-2 space-y-6">
                            <div className="space-y-6 max-w-5xl mx-auto">
                                <div className="bg-white flex items-center justify-center rounded-3xl px-6 py-5 shadow-xl shadow-gray-100/40 border border-gray-100">
                                    <SelectPetsSection
                                        title="Who's Attending Training Class?"
                                        subtitle="Choose from your available configuration dog profiles or establish a new profile"
                                        data={profilesFilteredByCategory}
                                        selectedId={selectedSavedPet?.id}
                                        onSelect={handleSavedPetSelect}
                                        showAddButton={true}
                                        onAddClick={handleAddPetClick}
                                        textContainer="flex items-center justify-center"
                                    />
                                </div>

                                {/* Context Dynamic Question Flow Card Anchor */}
                                <div ref={questionCardRef} className="scroll-mt-6">
                                    {showQuestionCard && bookingQuestions[questionStep] && (
                                        <QuestionCard
                                            currentStep={questionStep + 1}
                                            totalSteps={bookingQuestions.length}
                                            question={bookingQuestions[questionStep].question}
                                            type={bookingQuestions[questionStep].type as QuestionType}
                                            options={currentOptions}
                                            value={answers[bookingQuestions[questionStep].key as keyof typeof answers] as any}
                                            onChange={handleAnswer}
                                            onContinue={handleContinue}
                                        />
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* STAGE VIEW 2: CLASS TRAINING PACKAGES CANVAS */}
                        <div className="w-1/2 px-2 space-y-12">
                            <div className="flex flex-col space-y-6 max-w-7xl mx-auto">
                                <div className="text-center max-w-md mx-auto">
                                    <h2 className="text-xl md:text-2xl font-bold text-logoHeading tracking-tight">Available Training Modules</h2>
                                    <p className="text-xs text-gray-400 mt-1">Select code modules configured for your dog setup dimensions</p>
                                </div>

                                {filteredTrainingPackages.length > 0 ? (
                                    <div className="relative px-0 md:px-12">
                                        <button
                                            onClick={() => scrollSlider("left")}
                                            className="absolute left-2 md:left-0 top-1/2 -translate-y-1/2 z-40 bg-white/95 hover:bg-white text-gray-700 hover:text-mainBrandColor p-2 rounded-full shadow-xl border border-gray-100 transition-all duration-300 hover:scale-110 active:scale-95 cursor-pointer"
                                            aria-label="Scroll left"
                                        >
                                            <ChevronLeft size={16} className="sm:w-6 sm:h-6" strokeWidth={2.5} />
                                        </button>

                                        <div
                                            ref={trainingSliderRef}
                                            className="flex flex-row items-stretch overflow-x-auto scroll-smooth snap-x snap-mandatory [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden w-full pt-4"
                                        >
                                            {filteredTrainingPackages.map((item: any, index: number) => {
                                                const selectedPrice = item.pricing.find((price: any) => {
                                                    return price.label.toLowerCase() === `${currentPetSize?.toLowerCase()} dog` || price.label.toLowerCase() === "standard";
                                                }) || item.pricing[0];

                                                return (
                                                    <div
                                                        key={item.packageId || index}
                                                        className="w-full md:w-[50%] lg:w-1/3 shrink-0 snap-center md:snap-start px-2 md:px-3 snap-always"
                                                    >
                                                        <PackageCard
                                                            packageId={item.packageId}
                                                            packageTitle={item.packageTitle}
                                                            packageSubtitle={item.packageSubtitle}
                                                            description={item.description}
                                                            includes={item.includes}
                                                            pricing={{
                                                                pricingId: selectedPrice.pricingId,
                                                                size: currentPetSize,
                                                                label: selectedPrice.label || "",
                                                                price: selectedPrice.price || 0,
                                                                salePrice: selectedPrice.salePrice || 0,
                                                            }}
                                                            handleSelectPackage={() => {
                                                                navigate("/productpage/customergroomingbookingdatescreen", {
                                                                    state: {
                                                                        serviceType: "training",
                                                                        selectedPackage: {
                                                                            packageTitle: item.packageTitle,
                                                                            packageSubtitle: item.packageSubtitle,
                                                                            description: item.description,
                                                                            includes: item.includes,
                                                                            pricing: {
                                                                                pricingId: selectedPrice.pricingId,
                                                                                size: currentPetSize,
                                                                                label: selectedPrice.label || "",
                                                                                price: selectedPrice.price || 0,
                                                                                salePrice: selectedPrice.salePrice || 0,
                                                                            },
                                                                            petType: "dog",
                                                                            petSize: currentPetSize,
                                                                            savedPetInfo: selectedSavedPet || null
                                                                        },
                                                                    },
                                                                });
                                                            }}
                                                        />
                                                    </div>
                                                );
                                            })}
                                        </div>

                                        <button
                                            onClick={() => scrollSlider("right")}
                                            className="absolute right-2 md:right-0 top-1/2 -translate-y-1/2 z-40 bg-white/95 hover:bg-white text-gray-700 hover:text-mainBrandColor p-2 rounded-full shadow-xl border border-gray-100 transition-all duration-300 hover:scale-110 active:scale-95 cursor-pointer"
                                            aria-label="Scroll right"
                                        >
                                            <ChevronRight size={16} className="sm:w-6 sm:h-6" strokeWidth={2.5} />
                                        </button>
                                    </div>
                                ) : (
                                    <div className="text-center py-10 bg-white rounded-3xl border border-dashed border-gray-200 text-gray-400 text-sm mx-4">
                                        No training matrix structures listed matching this size classification profile.
                                    </div>
                                )}
                            </div>
                        </div>

                    </div>
                </div>
            </div>
        </div>
    );
};

export default PetTrainingScreen;